"use client";

import React, {
  Children,
  cloneElement,
  isValidElement,
} from "react";
import Footer from "../common/Footer";
import ToolLearnMoreInlineCta from "@/components/seo/ToolLearnMoreInlineCta";
import AutobotHeader from "@/components/layout/Header/CommonHeader";
import { ReviveMarketingToolbar } from "@/components/revive-landing/ReviveMarketingToolbar";
import { PAGE_SCROLL_ID } from "@/lib/scroll/pageScroll";

const CHROME_COMPONENTS = new Set([
  AutobotHeader,
  ReviveMarketingToolbar,
]);

const HEADER_SLOT_CLASS = "relative shrink-0 min-h-14 sm:min-h-16";

function isAutobotHeaderChild(child) {
  if (!isValidElement(child)) return false;
  if (child.type === AutobotHeader) return true;
  const name = child.type?.displayName || child.type?.name;
  return name === "AutobotHeader";
}

function isChromeChild(child) {
  if (!isValidElement(child)) return false;
  if (isAutobotHeaderChild(child)) return true;
  if (CHROME_COMPONENTS.has(child.type)) return true;
  const name = child.type?.displayName || child.type?.name;
  return name === "TokenStrengthBar";
}

function isMainElement(child) {
  return isValidElement(child) && child.type === "main";
}

function partitionPageChildren(children) {
  const chrome = [];
  const body = [];

  Children.forEach(children, (child) => {
    if (!child) return;

    if (isChromeChild(child)) {
      chrome.push(child);
      return;
    }

    if (isMainElement(child)) {
      const mainChildren = Children.toArray(child.props.children);
      const mainBody = [];
      let extractedChrome = false;

      for (const mainChild of mainChildren) {
        if (isChromeChild(mainChild)) {
          chrome.push(mainChild);
          extractedChrome = true;
        } else if (mainChild) {
          mainBody.push(mainChild);
        }
      }

      body.push(
        extractedChrome
          ? cloneElement(child, { key: child.key }, ...mainBody)
          : child,
      );
      return;
    }

    body.push(child);
  });

  /** If header matching failed by reference, pull it out of the scroll body. */
  const normalizedBody = [];
  for (const child of body) {
    if (isAutobotHeaderChild(child)) {
      chrome.unshift(child);
    } else {
      normalizedBody.push(child);
    }
  }

  return { chrome, body: normalizedBody };
}

function renderChromeItem(item, index) {
  const key = item.key ?? `page-chrome-${index}`;

  if (isAutobotHeaderChild(item)) {
    return (
      <div key={key} className={HEADER_SLOT_CLASS} data-idx-header-slot>
        {item}
      </div>
    );
  }

  return cloneElement(item, { key });
}

const PageLayout = ({ children, className = "" }) => {
  const { chrome, body } = partitionPageChildren(children);
  const hasHeaderSlot = chrome.some(isAutobotHeaderChild);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {chrome.length > 0 ? (
        <div className="relative z-[160] shrink-0">{chrome.map(renderChromeItem)}</div>
      ) : null}

      <div
        id={PAGE_SCROLL_ID}
        className={`idx-page-scroll min-h-0 flex-1 overflow-y-auto overflow-x-clip ${className}`}
        style={{ overflowAnchor: "none" }}
      >
        {/* Route-level <main> stays the single document landmark inside the scrollport. */}
        <div
          className={`min-w-0 max-w-full ${hasHeaderSlot ? "" : "pt-14 sm:pt-16"}`}
        >
          {body}
        </div>
        <ToolLearnMoreInlineCta />
        <Footer />
      </div>
    </div>
  );
};

export default PageLayout;
